import React, { useContext, createContext, useReducer } from "react";
import { useFormContext } from "lib/hooks/useFormContext";

export interface History {
  key: string;
  state: VisibilityStates;
}

type VisibilityStates = "hidden" | "open" | "closed" | "revisit";

type VisibleState = Record<string, VisibilityStates>; // undefined is the same as hidden

interface QuestionContextProviderProps {
  initVisibleState: VisibleState;
  children: React.ReactNode;
}

const lastIndexOf = (array: History[], { key, state }: History) => {
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i].key === key && array[i].state === state) return i;
  }
  return -1;
};

export class QuestionHelper<FormState extends Record<string, unknown>> {
  constructor(
    private setValue: (key: keyof FormState & string, value: unknown) => void,
    private visibleState: VisibleState,
    private history: History[],
    private getValues: <TKey extends keyof FormState>(
      key: TKey,
    ) => FormState[TKey],
    private changeVisibility: (
      key: keyof FormState & string,
      state: VisibilityStates,
    ) => void,
    private submitForm: () => void,
    private resetValues: (values?: Record<string, unknown>) => void,
  ) {}

  public close(key: keyof FormState & string): this {
    this.changeVisibility(key, "closed");
    return this;
  }

  public set<TKey extends keyof FormState & string>(
    key: TKey,
    value: FormState[TKey],
    state: VisibilityStates | null = "closed",
  ): QuestionHelper<FormState> {
    this.setValue(key, value);
    state && this.changeVisibility(key, state);
    return this;
  }

  public open(key: keyof FormState & string): QuestionHelper<FormState> {
    this.changeVisibility(key, "open");
    return this;
  }

  public submit(): void {
    this.submitForm();
  }

  public get<TKey extends keyof FormState>(key: TKey): FormState[TKey] {
    return this.getValues(key) as FormState[TKey];
  }

  public reset(
    values?: Record<string, unknown>,
    state: VisibilityStates | null = "closed",
  ) {
    this.resetValues(values);

    if (values && state) {
      for (const key of Object.keys(values)) {
        this.changeVisibility(key, state);
      }
    }

    return this;
  }

  public is(key: keyof FormState & string, state: VisibilityStates): boolean {
    return state === "hidden"
      ? !this.visibleState[key] || this.visibleState[key] === state
      : this.visibleState[key] === state;
  }

  public reOpen(key: keyof FormState & string): QuestionHelper<FormState> {
    const keys = this.extractPreceedingKeys({
      key: key,
      state: "closed",
    });
    keys.forEach((key) => {
      this.changeVisibility(key, "hidden");
    });
    this.changeVisibility(key, "open");

    return this;
  }

  public revisit(key: keyof FormState & string): QuestionHelper<FormState> {
    const lastOpened = this.history[this.history.length - 1];
    this.changeVisibility(lastOpened.key, "closed");
    this.changeVisibility(key, "open");
    return this;
  }

  private extractPreceedingKeys(step: History, filterBy?: VisibilityStates) {
    const lastIndex = lastIndexOf(this.history, step);
    const keys = [
      ...new Set(
        this.history
          .slice(lastIndex)
          .filter(({ state }) => (!filterBy ? true : state === filterBy))
          .map(({ key }) => key),
      ),
    ];
    return keys.filter((fKey) => fKey !== step.key);
  }
}

const QuestionContext = createContext({} as QuestionHelper<any>);

export const useQuestionContext = <
  FormState extends Record<string, unknown>,
>(): QuestionHelper<FormState> => {
  return useContext(QuestionContext);
};

export const QuestionContextProvider = <
  FormState extends Record<string, unknown>,
>({
  children,
  initVisibleState,
}: QuestionContextProviderProps): React.ReactElement => {
  const [visibleState, dispatch] = useReducer(
    (prev: VisibleState, next: VisibleState) => ({ ...prev, ...next }),
    initVisibleState,
  );

  const [history, pushHistory] = useReducer(
    (prev: History[], newHistory: History) => {
      // Only store the last 100 records to save bloat
      if (prev.length >= 100) return [...prev.slice(1, 99), newHistory];
      return [...prev, newHistory];
    },
    [
      Object.entries(initVisibleState).reduce(
        (prev, [key, state]) => ({ key, state }),
        {} as History,
      ),
    ],
  );

  const { setValue, submitForm, getValues, reset } =
    useFormContext<Record<string, unknown>>();

  const changeVisibility: QuestionHelper<FormState>["changeVisibility"] = (
    key,
    state,
  ) => {
    dispatch({ [key]: state });
    pushHistory({ key, state });
  };

  return (
    <QuestionContext.Provider
      value={
        new QuestionHelper(
          setValue,
          visibleState,
          history,
          getValues,
          changeVisibility,
          submitForm,
          reset,
        )
      }
    >
      {children}
    </QuestionContext.Provider>
  );
};
